import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Alert,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { PageTitle } from '../components/PageTitle';
import { tenantService } from '../services/tenantService';

const roleOptions = [
  { value: 'user', label: 'User' },
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Admin' },
];

interface TenantUser {
  user: {
    id: number;
    name: string;
    email: string;
    emailVerified: boolean;
    avatarUrl?: string;
  };
  roles: string[];
}

export const UserEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const tenantCode = tenantService.getCurrentTenantCode();

  const [tenantUser, setTenantUser] = useState<TenantUser | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      const response = await dataProvider.getOne('tenant-users', { id });

      if (response.data) {
        setTenantUser(response.data);
        setRoles(response.data.roles);
      } else {
        notify('User not found', { type: 'error' });
        navigate('/users');
      }
    } catch (error) {
      notify('Error loading user', { type: 'error' });
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenantUser) return;

    setSubmitting(true);

    try {
      await dataProvider.update('tenant-users', {
        id: tenantUser.user.id,
        data: { roles },
        previousData: tenantUser,
      });

      notify('User roles updated successfully', { type: 'success' });
      navigate('/users');
    } catch (error: any) {
      const message = error?.body?.message || 'Error updating user roles';
      notify(message, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageTitle
          title="Edit User"
          subtitle="Loading..."
          icon={<EditIcon />}
        />
      </Box>
    );
  }

  if (!tenantUser) {
    return (
      <Box>
        <PageTitle
          title="Edit User"
          subtitle="User not found"
          icon={<EditIcon />}
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Edit User"
        subtitle="Update user roles within the tenant"
        icon={<EditIcon />}
      />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mr: 2 }}
          >
            Back to Users
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Edit User Roles
          </Typography>

          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={tenantUser.user.avatarUrl}
                  sx={{ width: 64, height: 64 }}
                >
                  {tenantUser.user.name?.charAt(0) || tenantUser.user.email.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {tenantUser.user.name || 'Unnamed User'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography color="text.secondary">
                      {tenantUser.user.email}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {tenantUser.user.emailVerified ? '✓ Verified' : '⚠ Email not verified'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mb: 3 }}>
            Update the roles for this user within your tenant. Changes take effect immediately.
          </Alert>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={roles}
                  onChange={(e) => setRoles(e.target.value as string[])}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Warning:</strong> Be careful when removing admin privileges.
                  Ensure at least one admin remains in the tenant.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="button"
                  onClick={() => navigate('/users')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || roles.length === 0}
                  sx={{ backgroundColor: '#1C5195' }}
                >
                  {submitting ? 'Updating...' : 'Update Roles'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};